/**
 * MOQ Validator - Enhanced Version with Cart API Integration
 * 
 * This version uses Shopify's Cart API to enforce MOQ at multiple levels:
 * 1. Product page - prevent adding less than minimum
 * 2. Cart updates - validate on quantity change
 * 3. Checkout - final validation before proceeding
 */

(function() {
  'use strict';

  const CONFIG = {
    metafieldNamespace: 'odoo',
    metafieldKey: 'minimum_quantity',
    multiplierKey: 'multiplier',
    selectors: {
      quantityInput: 'input[name="quantity"], input[type="number"][id*="quantity"], .quantity-input, .quantity__input',
      addToCartButton: '[name="add"], button[type="submit"][name="add"], .product-form__submit',
      addToCartForm: 'form[action*="/cart/add"]',
      productJson: '[data-product-json], script[type="application/json"][data-product-json]',
      cartForm: 'form[action="/cart"]',
      cartQuantityInputs: 'input[name*="updates"]',
      checkoutButton: '[name="checkout"], .cart__checkout, button[name="checkout"]'
    }
  };

  // Store MOQ data for quick access
  let currentProductMOQ = null;
  let currentProductMultiplier = null;

  /**
   * Get minimum quantity from product metafields
   */
  function getMinimumQuantity() {
    try {
      const productJsonElement = document.querySelector(CONFIG.selectors.productJson);
      
      if (productJsonElement) {
        const productData = JSON.parse(productJsonElement.textContent);
        
        if (productData.metafields) {
          const moqMetafield = productData.metafields[CONFIG.metafieldNamespace]?.[CONFIG.metafieldKey];
          if (moqMetafield) {
            const minQty = parseInt(moqMetafield, 10);
            return isNaN(minQty) ? null : minQty;
          }
        }
      }

      const metaElement = document.querySelector(`[data-${CONFIG.metafieldNamespace}-${CONFIG.metafieldKey}]`);
      if (metaElement) {
        const minQty = parseInt(metaElement.dataset[`${CONFIG.metafieldNamespace}${CONFIG.metafieldKey.replace('_', '')}`], 10);
        return isNaN(minQty) ? null : minQty;
      }

      return null;
    } catch (error) {

      return null;
    }
  }

  /**
   * Get multiplier from product metafields
   */
  function getMultiplier() {
    try {
      const productJsonElement = document.querySelector(CONFIG.selectors.productJson);
      
      if (productJsonElement) {
        const productData = JSON.parse(productJsonElement.textContent);
        
        if (productData.metafields) {
          const multiplierMetafield = productData.metafields[CONFIG.metafieldNamespace]?.[CONFIG.multiplierKey];
          if (multiplierMetafield) {
            const multiplier = parseInt(multiplierMetafield, 10);
            return isNaN(multiplier) ? null : multiplier;
          }
        }
      }

      const metaElement = document.querySelector(`[data-${CONFIG.metafieldNamespace}-${CONFIG.multiplierKey}]`);
      if (metaElement) {
        const multiplier = parseInt(metaElement.dataset[`${CONFIG.metafieldNamespace}${CONFIG.multiplierKey}`], 10);
        return isNaN(multiplier) ? null : multiplier;
      }

      return null;
    } catch (error) {

      return null;
    }
  }

  /**
   * Get inventory data from product JSON
   */
  function getInventoryData() {
    try {
      const productJsonElement = document.querySelector(CONFIG.selectors.productJson);
      
      if (productJsonElement) {
        const productData = JSON.parse(productJsonElement.textContent);
        
        if (productData.inventory) {
          return {
            local_stock: parseInt(productData.inventory.local_stock, 10) || 0,
            remote_stock: parseInt(productData.inventory.remote_stock, 10) || 0,
            total_stock: parseInt(productData.inventory.total_stock, 10) || 0
          };
        }
      }
      
      return null;
    } catch (error) {
      return null;
    }
  }

  /**
   * Set minimum quantity on product page with strict enforcement
   * UNLESS stock is lower than MOQ - then MOQ is bypassed
   */
  function setMinimumQuantityOnProduct() {
    const minQuantity = getMinimumQuantity();
    const multiplier = getMultiplier();
    const inventory = getInventoryData();
    
    // Check if we should bypass MOQ due to low stock
    let effectiveMOQ = minQuantity;
    let bypassMOQ = false;
    
    if (minQuantity && inventory && inventory.total_stock > 0 && inventory.total_stock < minQuantity) {
      // Stock is lower than MOQ - bypass MOQ requirement
      effectiveMOQ = 1;
      bypassMOQ = true;
    }
    
    currentProductMOQ = bypassMOQ ? null : minQuantity;
    currentProductMultiplier = multiplier;
    
    if (!effectiveMOQ || effectiveMOQ === 1) {
      return;
    }

    const quantityInput = document.querySelector(CONFIG.selectors.quantityInput);
    
    if (quantityInput) {
      // Set default value to minimum quantity if needed (use effectiveMOQ)
      if (!quantityInput.value || parseInt(quantityInput.value, 10) < effectiveMOQ) {
        quantityInput.value = effectiveMOQ;
      }

      // STRICT: Prevent going below minimum (only if not bypassed)
      if (!bypassMOQ) {
        quantityInput.addEventListener('change', function(e) {
          const value = parseInt(this.value, 10);
          if (value < minQuantity) {
            this.value = minQuantity;
            showMessage(`Minimum antal er ${minQuantity} stk`, 'warning');
          }
        });

        quantityInput.addEventListener('input', function(e) {
          const value = parseInt(this.value, 10);
          if (value > 0 && value < minQuantity) {
            this.setCustomValidity(`Minimum antal er ${minQuantity} stk`);
          } else {
            this.setCustomValidity('');
          }
        });

        // Prevent form submission with invalid quantity
        quantityInput.addEventListener('invalid', function(e) {
          e.preventDefault();
          this.value = minQuantity;
          showMessage(`Minimum antal er ${minQuantity} stk`, 'error');
        });
      }
    }

    // Intercept Add to Cart form submission (only if not bypassed)
    if (!bypassMOQ) {
      interceptAddToCart(minQuantity);
    }

    addMinQuantityLabel(quantityInput, minQuantity);
  }

  /**
   * Intercept Add to Cart to enforce MOQ
   */
  function interceptAddToCart(minQuantity) {
    const forms = document.querySelectorAll(CONFIG.selectors.addToCartForm);
    
    forms.forEach(form => {
      form.addEventListener('submit', function(e) {
        const quantityInput = this.querySelector(CONFIG.selectors.quantityInput);
        const quantity = quantityInput ? parseInt(quantityInput.value, 10) : 1;

        if (quantity < minQuantity) {
          e.preventDefault();
          e.stopImmediatePropagation();
          
          quantityInput.value = minQuantity;
          showMessage(`Du skal minimum bestille ${minQuantity} stk af dette produkt`, 'error');
          
          return false;
        }
      });
    });
  }

  /**
   * Fix theme quantity +/- buttons to use multiplier
   */
  function fixQuantityButtons(quantityInput, minQuantity, multiplier) {
    if (!quantityInput) {

      return;
    }

    // Find quantity wrapper - try multiple approaches
    let quantityWrapper = quantityInput.closest('quantity-input');
    if (!quantityWrapper) {
      quantityWrapper = quantityInput.closest('.quantity');
    }
    if (!quantityWrapper) {
      quantityWrapper = quantityInput.closest('.quantity-selector');
    }
    if (!quantityWrapper) {
      quantityWrapper = quantityInput.parentElement;
    }

    if (!quantityWrapper) {

      return;
    }

    // Find all buttons in wrapper
    const allButtons = quantityWrapper.querySelectorAll('button');

    let plusButton = null;
    let minusButton = null;

    // Try to identify plus/minus buttons
    allButtons.forEach((btn, index) => {
      const name = btn.getAttribute('name');
      const ariaLabel = btn.getAttribute('aria-label') || '';
      const className = btn.className || '';

      // Plus button detection
      if (name === 'plus' || 
          ariaLabel.toLowerCase().includes('increase') || 
          ariaLabel.toLowerCase().includes('øg') ||
          className.includes('plus')) {
        plusButton = btn;

      }

      // Minus button detection
      if (name === 'minus' || 
          ariaLabel.toLowerCase().includes('decrease') || 
          ariaLabel.toLowerCase().includes('reducer') ||
          className.includes('minus')) {
        minusButton = btn;

      }
    });

    // Fallback: if we have exactly 2 buttons, assume first is minus, second is plus
    if (!plusButton && !minusButton && allButtons.length === 2) {
      minusButton = allButtons[0];
      plusButton = allButtons[1];

    }

    if (plusButton) {

      plusButton.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const currentValue = parseInt(quantityInput.value, 10) || minQuantity;
        const newValue = currentValue + multiplier;

        // Update the value directly
        quantityInput.value = newValue;
        
        // Try setting via setAttribute as well (for custom elements)
        quantityInput.setAttribute('value', newValue);
        
        // Dispatch multiple events to ensure theme picks it up
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        
        // Force update custom element if it exists
        const customElement = quantityInput.closest('quantity-input');
        if (customElement && customElement.setValue) {
          customElement.setValue(newValue);
        }

        return false;
      }, true);
    } else {

    }

    if (minusButton) {

      minusButton.addEventListener('click', function(e) {
        e.preventDefault();
        e.stopPropagation();
        e.stopImmediatePropagation();
        
        const currentValue = parseInt(quantityInput.value, 10) || minQuantity;
        const newValue = Math.max(minQuantity, currentValue - multiplier);
        
        // Update the value directly
        quantityInput.value = newValue;
        
        // Try setting via setAttribute as well (for custom elements)
        quantityInput.setAttribute('value', newValue);
        
        // Dispatch multiple events to ensure theme picks it up
        quantityInput.dispatchEvent(new Event('change', { bubbles: true }));
        quantityInput.dispatchEvent(new Event('input', { bubbles: true }));
        
        // Force update custom element if it exists
        const customElement = quantityInput.closest('quantity-input');
        if (customElement && customElement.setValue) {
          customElement.setValue(newValue);
        }

        return false;
      }, true);
    } else {

    }
  }

  /**
   * Add minimum quantity label (removed - not needed per user request)
   */
  function addMinQuantityLabel(quantityInput, minQuantity) {
    // Label disabled per user request
    return;
  }

  /**
   * Fetch product data by handle to get MOQ
   */
  async function getProductMOQ(handle) {
    try {
      const response = await fetch(`/products/${handle}.js`);
      const product = await response.json();
      
      const moqMetafield = product.metafields?.odoo?.minimum_quantity;
      
      if (moqMetafield) {
        const minQuantity = parseInt(moqMetafield, 10);
        return isNaN(minQuantity) ? null : minQuantity;
      }
      
      return null;
    } catch (error) {

      return null;
    }
  }

  /**
   * Validate cart quantities with detailed error reporting
   */
  async function validateCartQuantities() {
    try {
      const response = await fetch('/cart.js');
      const cart = await response.json();

      const errors = [];
      const validationPromises = [];

      for (const item of cart.items) {
        validationPromises.push(
          getProductMOQ(item.handle).then(minQuantity => {
            if (minQuantity && item.quantity < minQuantity) {
              errors.push({
                key: item.key,
                title: item.product_title,
                variant: item.variant_title,
                current: item.quantity,
                minimum: minQuantity,
                handle: item.handle
              });
            }
          })
        );
      }

      await Promise.all(validationPromises);

      return {
        valid: errors.length === 0,
        errors,
        cart
      };
    } catch (error) {

      return { valid: true, errors: [], cart: null };
    }
  }

  /**
   * Show message to user with auto-dismiss
   */
  function showMessage(message, type = 'info') {
    const existingMessages = document.querySelectorAll('.moq-message');
    existingMessages.forEach(msg => msg.remove());

    const messageDiv = document.createElement('div');
    messageDiv.className = `moq-message moq-message--${type}`;
    messageDiv.innerHTML = `
      <div style="display: flex; align-items: center; gap: 0.75rem;">
        <span style="font-size: 1.5rem;">${type === 'error' ? '⛔' : type === 'warning' ? '⚠️' : '✓'}</span>
        <span>${message}</span>
      </div>
    `;
    messageDiv.style.cssText = `
      position: fixed;
      top: 20px;
      right: 20px;
      padding: 1rem 1.5rem;
      background: ${type === 'error' ? '#dc3545' : type === 'warning' ? '#ffc107' : '#28a745'};
      color: ${type === 'warning' ? '#000' : '#fff'};
      border-radius: 8px;
      box-shadow: 0 4px 12px rgba(0,0,0,0.2);
      z-index: 10000;
      font-size: 0.95rem;
      max-width: 450px;
      animation: slideIn 0.3s ease-out;
      font-weight: 500;
    `;

    document.body.appendChild(messageDiv);

    setTimeout(() => {
      messageDiv.style.animation = 'slideOut 0.3s ease-in';
      setTimeout(() => messageDiv.remove(), 300);
    }, 5000);
  }

  /**
   * Show detailed cart validation errors with fix option
   */
  function showCartErrors(errors) {
    const errorList = errors.map(err => {
      const variant = err.variant ? ` (${err.variant})` : '';
      const diff = err.minimum - err.current;
      return `${err.title}${variant}: Du mangler ${diff} stk - minimum er ${err.minimum} stk (nuværende: ${err.current})`;
    }).join('\n');

    const message = `MINIMUMSKRAV IKKE OPFYLDT\n\n${errorList}\n\nOpdater venligst antal i kurven før du går til kassen.`;
    
    // Use the existing showMessage function for better UX
    showMessage(message, 'error');

    // Optionally: Auto-fix quantities (uncomment if desired)
    // autoFixCartQuantities(errors);
  }

  /**
   * Auto-fix cart quantities to minimum (optional feature)
   */
  async function autoFixCartQuantities(errors) {
    for (const error of errors) {
      try {
        await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            id: error.key,
            quantity: error.minimum
          })
        });
      } catch (e) {

      }
    }
    
    showMessage('Antal er opdateret til minimum krav', 'info');
    setTimeout(() => window.location.reload(), 1500);
  }

  /**
   * Intercept checkout button with strict validation
   */
  function interceptCheckout() {
    const checkoutButtons = document.querySelectorAll(CONFIG.selectors.checkoutButton);

    checkoutButtons.forEach(button => {
      // Remove any existing listeners
      const newButton = button.cloneNode(true);
      button.parentNode.replaceChild(newButton, button);

      newButton.addEventListener('click', async function(event) {
        event.preventDefault();
        event.stopImmediatePropagation();

        // Show loading state
        const originalText = this.textContent;
        this.textContent = 'Validerer...';
        this.disabled = true;

        const validation = await validateCartQuantities();

        if (!validation.valid) {
          this.textContent = originalText;
          this.disabled = false;
          showCartErrors(validation.errors);
          return false;
        }

        // Validation passed - proceed to checkout
        this.textContent = originalText;
        this.disabled = false;
        
        // Submit the form or redirect to checkout
        const form = this.closest('form');
        if (form) {
          form.submit();
        } else {
          window.location.href = '/checkout';
        }
      }, true); // Use capture phase for highest priority
    });
  }

  /**
   * Monitor cart quantity changes
   */
  function monitorCartQuantityChanges() {
    const quantityInputs = document.querySelectorAll(CONFIG.selectors.cartQuantityInputs);
    
    quantityInputs.forEach(input => {
      input.addEventListener('change', async function() {
        const lineKey = this.name.replace('updates[', '').replace(']', '');
        const newQuantity = parseInt(this.value, 10);
        
        // Get item handle from data attribute or row
        const row = this.closest('tr, .cart-item');
        const handle = row?.dataset?.handle;
        
        if (handle) {
          const moq = await getProductMOQ(handle);
          
          if (moq && newQuantity < moq) {
            this.value = moq;
            showMessage(`Minimum antal for dette produkt er ${moq} stk`, 'warning');
            
            // Update cart via API
            await fetch('/cart/change.js', {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify({
                id: lineKey,
                quantity: moq
              })
            });
          }
        }
      });
    });
  }

  /**
   * Add CSS animations
   */
  function addAnimations() {
    if (document.getElementById('moq-animations')) return;

    const style = document.createElement('style');
    style.id = 'moq-animations';
    style.textContent = `
      @keyframes slideIn {
        from {
          transform: translateX(120%);
          opacity: 0;
        }
        to {
          transform: translateX(0);
          opacity: 1;
        }
      }
      
      @keyframes slideOut {
        from {
          transform: translateX(0);
          opacity: 1;
        }
        to {
          transform: translateX(120%);
          opacity: 0;
        }
      }
    `;
    document.head.appendChild(style);
  }

  /**
   * Initialize MOQ validator
   */
  function init() {    
    addAnimations();

    // On product pages
    if (document.body.classList.contains('template-product') || 
        document.querySelector('[data-section-type="product"]') ||
        document.querySelector('.product-form')) {
      setMinimumQuantityOnProduct();
    }

    // On cart page
    if (document.body.classList.contains('template-cart') || 
        window.location.pathname.includes('/cart')) {
      interceptCheckout();
      monitorCartQuantityChanges();
    }

    // Listen for theme-specific cart events
    document.addEventListener('cart:updated', function() {
      interceptCheckout();
      monitorCartQuantityChanges();
    });

    // Listen for AJAX cart drawer opens
    document.addEventListener('cart:open', function() {
      setTimeout(() => {
        interceptCheckout();
      }, 100);
    });
  }

  // Initialize when DOM is ready
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Re-init on Shopify section renders (for theme editor)
  document.addEventListener('shopify:section:load', init);

  // Global API for debugging and manual control
  window.MOQValidator = {
    getMinimumQuantity,
    validateCartQuantities,
    showMessage,
    getCurrentMOQ: () => currentProductMOQ,
    version: '2.0-enhanced'
  };

})();
